// App.js - Updated to work with Amplify Auth
import React, { useState } from 'react';
import { Amplify } from 'aws-amplify';
import amplifyconfig from './amplifyconfiguration.json';
import LoginScreen, { useAuth } from './LoginScreen';
import Sidebar from './Sidebar';
import ModernMultiStepEventForm from './components/ModernMultiStepEventForm';
import AdminModal from './components/AdminModal';
import { isAdmin } from './utils/userUtils';
import './App.css';

// Configure Amplify - this should be done once at the top level
Amplify.configure(amplifyconfig);

function App() {
  // Note: useAuth now returns 'signOut' instead of 'logout', and 'user' object
  const { isAuthenticated, isLoading, user, signOut } = useAuth();

  // Determine admin visibility for the cog using centralized helper
  // const isAdminUser = isAdmin(user); // not used; rely on inline checks below
  const [currentStep, setCurrentStep] = useState(1);
  const [stepValidation, setStepValidation] = useState({});
  const [visitedSteps, setVisitedSteps] = useState(new Set([1]));
  const [isAdminOpen, setIsAdminOpen] = useState(false);

  const handleStepValidationChange = (validation) => {
    setStepValidation(validation);
  };

  const handleVisitedStepsChange = (visited) => {
    setVisitedSteps(visited);
  };

  const handleCurrentStepChange = (step) => {
    setCurrentStep(step);
  };

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        // Keep spinner overlay consistent with reverted grey theme
        background: 'linear-gradient(135deg, #F3F4F6 0%, #D1D5DB 50%, #9CA3AF 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 999999
      }}>
        <div style={{
          background: 'white',
          borderRadius: '0.75rem',
          padding: '2rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)'
        }}>
          <div style={{
            width: '2rem',
            height: '2rem',
            border: '3px solid #e2e8f0',
            borderTop: '3px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <span style={{ fontSize: '1rem', color: '#374151' }}>Loading...</span>
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginScreen />;
  }


  return (
    <div className="app-container" style={{
      // Reverted to original grey gradient background
      background: 'linear-gradient(135deg, #F3F4F6 0%, #D1D5DB 50%, #9CA3AF 100%)',
      minHeight: '100vh',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Large yellow star in bottom right */}
      <div style={{
        position: 'fixed',
        bottom: '-70px',
        right: '-70px',
        width: '560px',
        height: '560px',
        opacity: 0.45,
        pointerEvents: 'none',
        zIndex: 0
      }}>
        <img 
          src={require('./assets/efs_star_large.png')} 
          alt="" 
          style={{ 
            width: '100%', 
            height: '100%', 
            objectFit: 'contain',
            imageRendering: 'smooth'
          }}
        />
      </div>

      <div className="card" style={{ position: 'relative', zIndex: 1 }}>
        {/* User info display (optional) */}
        {user && (
          <div style={{
            position: 'absolute',
            top: '1.5rem',
            right: '10rem',
            zIndex: 50,
            fontSize: '14px',
            color: '#64748b'
          }}>
            Welcome, {user.username}
          </div>
        )}

        {/* Admin Cog (top-right) + Sign Out Button */}
        {user && (user.attributes && (user.attributes['custom:role'] === 'superadmin' || (user.attributes['custom:roles'] && user.attributes['custom:roles'].includes('admin')))) && (
          <div style={{ position: 'absolute', top: '1.5rem', right: '9.5rem', zIndex: 50 }}>
            <button
              onClick={() => setIsAdminOpen(true)}
              title="Admin"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: 'transparent',
                color: '#111827',
                padding: '8px 10px',
                border: '1px solid rgba(0,0,0,0.06)',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                boxShadow: '0 2px 4px rgba(0,0,0,0.04)'
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 15.5A3.5 3.5 0 1 0 12 8.5a3.5 3.5 0 0 0 0 7z"></path>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06A2 2 0 1 1 2.3 16.9l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09c.65 0 1.2-.4 1.51-1a1.65 1.65 0 0 0-.33-1.82L3.3 4.7A2 2 0 1 1 6.13 1.87l.06.06a1.65 1.65 0 0 0 1.82.33H8a1.65 1.65 0 0 0 1-1.51V1a2 2 0 1 1 4 0v.09c0 .66.4 1.2 1 1.51h.09a1.65 1.65 0 0 0 1.82-.33l.06-.06A2 2 0 1 1 21.7 7.1l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09c-.65 0-1.2.4-1.51 1z"></path>
              </svg>
              Admin
            </button>
          </div>
        )}

        {/* Sign Out Button - Top Right */}
        <div style={{
          position: 'absolute',
          top: '1.5rem',
          right: '1.5rem',
          zIndex: 50
        }}>
          <button
            onClick={signOut} // Changed from 'logout' to 'signOut'
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: '#ef4444',
              color: 'white',
              padding: '10px 20px',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              minHeight: '40px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#dc2626';
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = '#ef4444';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
            }}
            title="Sign Out"
          >
            {/* Sign Out Icon (SVG) */}
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16,17 21,12 16,7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            Sign Out
          </button>
        </div>

        <Sidebar 
          currentStep={currentStep} 
          stepValidation={stepValidation}
          visitedSteps={visitedSteps}
        />
        
        <div className="form">
      <ModernMultiStepEventForm 
        username={user?.username}
        authUser={user}
            onStepValidationChange={handleStepValidationChange}
            onVisitedStepsChange={handleVisitedStepsChange}
            onCurrentStepChange={handleCurrentStepChange}
          />
        </div>
        <AdminModal isOpen={isAdminOpen} onClose={() => setIsAdminOpen(false)} />
      </div>
    </div>
  );
}

export default App;